import { useState, useRef, useLayoutEffect } from "react";
import { UUID } from "@screencloud/uuid";
import { Styled } from "src/components/ContentAuditLog/styles";
import { ObjectType } from "src/helpers/logHelper";
import {
  Breadcrumb,
  BreadcrumbDivider,
  BreadcrumbSection,
} from "@screencloud/screencloud-ui-components";
import { LogDetails } from "src/components/LogItem/components/LogDetails";
import { FormattedMessage } from "react-intl";
import { LogFilter } from "./LogFilter";
import { useAuditLogFilter } from "src/hooks/useAuditLogFilter";
import { Log } from "src/types.g";
import { useUserById } from "src/hooks/useUserById";
import { LogList } from "./LogList";

interface ContentAuditLogProps {
  entityId: UUID;
  entityType: ObjectType;
}

export const ContentAuditLog = (props: ContentAuditLogProps): JSX.Element => {
  const {
    handleFilterChange,
    filters,
    isPersonDropdownOpen,
    handlePersonDropdownOpen,
  } = useAuditLogFilter();

  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Scroll position preservation
  const logContentRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number>(0);

  /*
   * Restore scroll position when view changes
   * useLayoutEffect runs before paint
   * so we can restore the scroll position before the user sees the change
   */
  useLayoutEffect(() => {
    if (!logContentRef.current) return;

    if (showDetails) {
      logContentRef.current.scrollTop = 0;
    } else {
      logContentRef.current.scrollTop = savedScrollPosition.current;
    }
  }, [showDetails]);

  const { user } = useUserById({
    id: selectedLog?.userId,
  });

  const finalFilters = {
    ...filters,
    entityId: props.entityId,
    entityTypes: [props.entityType],
  };

  const handleLogItemClick = (log: Log) => {
    // Save current scroll position before showing details
    if (logContentRef.current) {
      savedScrollPosition.current = logContentRef.current.scrollTop;
    }
    setSelectedLog(log);
    setShowDetails(true);
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setSelectedLog(null);
  };

  /*
   * Always render the both views (list and details), use CSS to show/hide content
   * This prevents re-rendering to preserve component state (filters, scroll position, etc.)
   * when switching between list and details views
   */
  return (
    <Styled>
      <div className="modal-header">
        {showDetails && selectedLog ? (
          <Breadcrumb>
            <BreadcrumbSection onClick={handleBackToList}>
              Activity
            </BreadcrumbSection>
            <BreadcrumbDivider />
            <BreadcrumbSection active>Activity Details</BreadcrumbSection>
          </Breadcrumb>
        ) : (
          <FormattedMessage
            id="audit_log.content_detail.modal.activity.title"
            defaultMessage="Activity"
          />
        )}
      </div>
      <div style={{ display: showDetails ? "none" : "block" }}>
        <LogFilter
          filters={filters}
          handleFilterChange={handleFilterChange}
          isPersonDropdownOpen={isPersonDropdownOpen}
          handlePersonDropdownOpen={handlePersonDropdownOpen}
        />
      </div>

      <div className="log-content" ref={logContentRef}>
        <div style={{ display: showDetails ? "none" : "block" }}>
          <LogList filters={finalFilters} onLogItemClick={handleLogItemClick} />
        </div>

        <div style={{ display: showDetails && selectedLog ? "block" : "none" }}>
          {selectedLog && <LogDetails log={selectedLog} user={user} />}
        </div>
      </div>
    </Styled>
  );
};
